import { AfterViewInit, Component, OnDestroy, OnInit, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TestLeftMenu, TestProvider } from '../../providers/test-provider';
import { UsersProvider } from '../../../users/providers/users-provider';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { TranslateService } from '@ngx-translate/core';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { Subscription } from 'rxjs';
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { User } from "../../../common/models/user";
import { Test } from "../../../common/models/test";
import { LangsProvider } from "../../../common/providers/langs-provider";
import { CurrentUser } from "../../../common/providers/current-user";
import { OrganizationRole } from "../../../common/models/organization-role";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { TestSideMenuComponent } from "../../components/side-menu/side-menu";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { NgIf } from "@angular/common";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { TestGenericContainerComponent } from "../generic-container/generic-container.component";
import { mapTestItemRightsToRowRights } from "../../providers/test-rights";

@Component({
  selector: 'app-test-container',
  templateUrl: './test-container.component.html',
  styleUrls: ['./test-container.scss'],
  imports: [
    PageContentComponent,
    HeaderComponent,
    SideMenuComponent,
    TestSideMenuComponent,
    Error401Component,
    NgIf,
    ModuleContentComponent,
    Error404Component,
    TestGenericContainerComponent
  ],
  standalone: true
})
export class TestContainerComponent implements OnInit, AfterViewInit, OnDestroy {
    public pageTitle = 'Tests';
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public organization: Organization = {} as Organization;
    public athleteUser: User = {} as User;
    public tests: Test[] = [];
    public test: Test = {} as Test;
    public testsLeftMenu: TestLeftMenu[] = [];
    public activeTestId: number | null = null;
    public testsChange = new EventEmitter<void>();
    private subscriptions: Subscription[] = [];

    constructor(
        private currentUser: CurrentUser,
        private currentUserRoles: CurrentUserRolesProvider,
        private route: ActivatedRoute,
        private testProvider: TestProvider,
        private langsProvider: LangsProvider,
        private userProvider: UsersProvider,
        private breadcrumbProvider: BreadcrumbProvider,
        private translator: TranslateService,
        private cdr: ChangeDetectorRef,
    ) {
    }

    public ngOnInit(): void {
        // Load athlete and annual plan if it exists
        this.subscriptions.push(
            this.route.data.subscribe((data: any) => {//{ organization: Organization; athlete: Athlete; test: Test }
                if (data.organization) {
                    this.organization = data.organization;
                }

                if (data.athlete) {
                    this.athleteUser = data.athlete;
                }

                if (data.test) {
                    this.test = data.test;
                    this.activeTestId = this.test.id!;
                }

                this.loadData();

            })
        );
        this.subscriptions.push(
            this.testsChange.subscribe(() => {
                this.updateSideMenuEvent(this.activeTestId);
            })
        );
    }

    public ngAfterViewInit(): void {

    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public loadData() {
        this.testProvider.findAllUniqueWithMaxDate(this.organization, this.athleteUser, this.langsProvider.getBestLangId()).subscribe({
            next: (testsLeftMenu) => {
                this.testsLeftMenu = testsLeftMenu;
                this.createBreadcrumb();
            },
            error: (error) => {
              console.error("TestContainer : ", error);
            }, complete: () => {
              this.isLoading = false;
              this.cdr.detectChanges();
          }
        });
    }

    public updateSideMenuEvent(activeTestId: number | null) {
        this.activeTestId = activeTestId;
        this.loadData();
    }

    private checkRights (test: any) {
        let rights = {
            edit: false,
            delete: false,
        }
        if (!this.organization.id || !this.currentUser.user?.id) return ;
        if (test.read_only)
            return rights;
        if (this.currentUserRoles.have(OrganizationRole.ID_COACH, this.organization.id)) {
            rights = {
                edit: test.creater_id === this.currentUser.user.id,
                delete: false,
            }
        }
        if (this.currentUserRoles.have(OrganizationRole.ID_SUPERCOACH, this.organization.id)) {
            rights = {
                edit: true,
                delete: true,
            }
        }
        if (this.currentUserRoles.have(OrganizationRole.ID_ADMIN, this.organization.id)) {
            rights = {
                edit: true,
                delete: true,
            }
        }
        return rights;
    }

    private createBreadcrumb() {
      if (!this.organization.id) return;
        const crumbs: Crumb[] = [];
        if (!this.currentUserRoles.have(OrganizationRole.ID_ATHLETE, this.organization.id)){
            crumbs.push({
                shortTitle: 'Athletes',
                title: 'Athletes',
                url: '/' + this.organization!.code + '/athletes'
            });
        }
        crumbs.push({
            shortTitle: `${this.athleteUser.firstName?.substr(0, 1)}. ${this.athleteUser.lastName}`,
            title: this.athleteUser.firstName + ' ' + this.athleteUser.lastName,
            url: `/${this.organization!.code}/athletes/${this.athleteUser.id}`
        });
        crumbs.push({
            shortTitle: 'Tests',
            title: 'Tests',
            url: `/${this.organization!.code}/athletes/${this.athleteUser.id}/tests`
        });
        crumbs.push({
            shortTitle: this.test.name ?? '',
            title: this.test.name ?? '',
        });
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }
}
